import type { ReactNode } from "react";

const HOME = "https://ottercraft.dev";

/**
 * The static, crawlable half of the page: what OtterDrop is, how a room works,
 * and the handful of questions that actually come up. Pure markup with no
 * runtime state, so it is prerendered into index.html at build time (see
 * prerender.tsx) and rendered again by the app on mount.
 *
 * Deliberately short — it sits under a tool, not on a landing page. The
 * long-form guides live on ottercraft.dev; this only needs to say what the
 * thing is. Questions are collapsed <details> rows: indexable, one line each.
 */
export function Explainer() {
  return (
    <section id="about" aria-labelledby="about-title" className="mt-16 border-t border-rule pt-12">
      <p className="eyebrow">About OtterDrop</p>
      <h2
        id="about-title"
        className="mt-2 max-w-[600px] font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-pine sm:text-[30px]"
      >
        Files go straight from one device to the other.
      </h2>
      <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.6] text-soft">
        If your phone and your laptop are on the same Wi-Fi, they can hand files to each other
        without a cable, a cloud account or an app. Open this page on both and they find each other.
        Share a file on one, take it on the other. It works between iPhone, Android, Windows, macOS
        and Linux, in any combination.
      </p>

      <ol className="mt-8 grid gap-6 sm:grid-cols-3">
        <Step n="01" title="Open the page on two devices">
          Every device on your network lands in the same room. From a phone, the quickest way in
          is to scan the QR code on the other screen — that works whether or not the phone is on
          the same Wi-Fi. For someone further away, send the link or read out the four-character
          room code.
        </Step>
        <Step n="02" title="Share a file">
          Drop it on the page. It shows up in the list on every device in the room, but nothing has
          been sent yet. Sharing just announces the file.
        </Step>
        <Step n="03" title="Take it on the other device">
          Tap the file, or <em>Download all</em>. It comes directly from the device that shared it,
          at Wi-Fi speed. Any type of file, any size.
        </Step>
      </ol>

      <p className="mt-8 max-w-[680px] text-[13.5px] leading-[1.6] text-soft">
        Underneath it&rsquo;s WebRTC, the same encrypted peer-to-peer connection that browser video
        calls use. A small signalling service passes the connection handshake between devices so
        they can find each other. It never sees a file. No server keeps anything and there is no
        history. Close the tab and what you shared is gone. A room takes up to eight devices.
      </p>

      <h3 id="faq" className="mt-12 scroll-mt-6 font-serif text-[20px] font-semibold leading-tight text-pine">
        Questions and troubleshooting
      </h3>
      <div className="mt-3 divide-y divide-rule border-y border-rule">
        <Faq q="I don't see the other device">
          Devices find each other automatically when they share a{" "}
          <strong className="font-semibold text-ink">Wi-Fi network</strong>. If they don&rsquo;t —
          a VPN, or a guest network that keeps devices apart — scan the QR code, or type the
          other screen&rsquo;s <strong className="font-semibold text-ink">room code</strong> under{" "}
          <em>Enter a code</em>. If you see &ldquo;Could not reach the room server&rdquo;, the
          network is blocking the connection. Reconnect and refresh.
        </Faq>
        <Faq q="Can I send to someone in another city?">
          Yes — send them the link, or read them the four-character room code to type under{" "}
          <em>Enter a code</em>. It opens the same room, and the file still goes straight from
          your device to theirs; it is never uploaded to us. That is also the catch: we don&rsquo;t run a relay to fall back on, so if either
          network blocks direct connections (some office Wi-Fi, some mobile carriers) the two
          devices can&rsquo;t connect at all.
        </Faq>
        <Faq q="A device shows “adrift”, or a download fails">
          The two devices couldn&rsquo;t connect to each other directly.{" "}
          <strong className="font-semibold text-ink">Hotel, guest and office Wi-Fi</strong> often
          block that on purpose, and we have no relay to route around it — that is the price of files
          never touching our servers. Try a phone hotspot or a home network.
        </Faq>
        <Faq q="Who can join with my room code?">
          Anyone who has it, from any network — that is the point of it, and the reason it is
          worth treating like a door key rather than a label. It is only good while the room is:
          once every device has closed the page there is nothing left to join, and the code lapses
          on its own and can end up belonging to somebody else&rsquo;s room.
        </Faq>
        <Faq q="A transfer stops partway">
          The file comes straight from the device that shared it, so that device has to{" "}
          <strong className="font-semibold text-ink">keep this page open</strong> until the download
          is done. Locking a phone can pause its browser, too.
        </Faq>
        <Faq q="Can anyone on my Wi-Fi see my files?">
          Anyone on the same network can join the room, see the names of the files on offer, and
          take them. At home that&rsquo;s the point. On a busy shared network, look at the device names
          before you take something, and don&rsquo;t share what you wouldn&rsquo;t want the room to
          see. Nobody outside the two devices in a transfer can see the file contents.
        </Faq>
        <Faq q="Does it need the internet?">
          Only briefly, to set things up. The files themselves go over your local Wi-Fi. A home
          router or a phone hotspot is enough.
        </Faq>
      </div>

      <p className="mt-6 text-[13px] leading-[1.6] text-muted">
        Step-by-step:{" "}
        <a className="link" href={`${HOME}/guides/share-files-between-phone-and-computer`}>
          sharing files between your phone and computer
        </a>
        . OtterDrop is part of <a className="link" href={HOME}>ottercraft.dev</a>, a few small free
        tools that run in your browser.
      </p>
    </section>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1">
      <span className="font-mono text-[11px] text-teal-deep">{n}</span>
      <span className="font-serif text-[16px] font-semibold leading-tight text-pine">{title}</span>
      <p className="text-[13px] leading-[1.55] text-soft">{children}</p>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-serif text-[16px] leading-tight text-pine [&::-webkit-details-marker]:hidden">
        {q}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-muted transition-transform group-open:rotate-45" aria-hidden="true">
          <path d="M12 5v14" /><path d="M5 12h14" />
        </svg>
      </summary>
      <p className="mt-2 max-w-[720px] text-[14px] leading-[1.6] text-soft">{children}</p>
    </details>
  );
}
